import type { PresenceType } from 'react-idle-timer';
import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { TimeoutModal } from '@frontend/store/TimeoutModal';
import { useAbortableFetch } from '@frontend/utils';
import { useFrontendSettings } from '@frontend/utils/hooks/useFrontendSettings';

export function Timeout() {
    const abortableFetch = useAbortableFetch();
    const { frontendSettings } = useFrontendSettings();

    // timer that is called periodically to extend the session while the user is active
    const redirectUrl = `${location.pathname}${location.search}`;

    const maxSessionInMiliseconds = frontendSettings.sessionDurationMinutes * 60 * 1000;
    const showWarningInMiliseconds = frontendSettings.sessionDurationWarningMinutes * 60 * 1000;
    const [sessionState, setSessionState] = useState<'Active' | 'Idle' | 'Prompted'>('Active');
    const [open, setOpen] = useState(false);
    const [numberOfExtendFailures, setNumberOfExtendFailures] = useState(0);
    const maxNumberOfExtendFailures = 3;

    useEffect(() => {
        if (open) {
            datadogRum.addAction('show-timeout-modal');
        }
    }, [open]);

    const { reset, activate, isPrompted, isIdle } = useIdleTimer({
        crossTab: true,
        debounce: 2000,
        events: [
            'mousemove',
            'keydown',
            'wheel',
            'DOMMouseScroll',
            'mousewheel',
            'mousedown',
            'touchstart',
            'touchmove',
            'MSPointerDown',
            'MSPointerMove',
        ],
        eventsThrottle: 100,
        leaderElection: true,
        onAction,
        onPresenceChange,
        promptBeforeIdle: showWarningInMiliseconds,
        startOnMount: true,
        stopOnIdle: true,
        syncTimers: 100,
        throttle: 0,
        timeout: maxSessionInMiliseconds,
    });

    function extendSessionApiCall() {
        return abortableFetch.fetchJson('/api/session/extend', 'POST');
    }

    function failedToExtendSession() {
        if (numberOfExtendFailures >= maxNumberOfExtendFailures) {
            setSessionState('Idle');
            setOpen(true);
            datadogRum.addAction('session-extensions-failed-show-logout-modal');
        }
        setNumberOfExtendFailures(numberOfExtendFailures + 1);
    }

    async function extendSession() {
        try {
            const result = await extendSessionApiCall();

            if (result.status === 200) {
                activate();
                setOpen(false);
                setNumberOfExtendFailures(0);
            } else {
                failedToExtendSession();
            }
        } catch {
            failedToExtendSession();
        }
    }

    async function onAction(_event?: Event) {
        // Do something when a user triggers a watched event
        if (!isPrompted() || !isIdle()) {
            await extendSession();
            reset();
        }
    }

    async function onPresenceChange(presence: PresenceType) {
        // Handle state changes in one function
        if (presence.type === 'active') {
            if (presence.prompted) {
                setOpen(true);
                setSessionState('Prompted');
            } else {
                setOpen(false);
                setSessionState('Active');
                await extendSession();
            }
        } else if (presence.type === 'idle') {
            setOpen(true);
            setSessionState('Idle');
        }
    }

    async function handleOnLogoutUser() {
        try {
            // try to extend the session of user for better UX so they don't need to type in their credentials again
            const result = await extendSessionApiCall();

            if (result?.status === 200) {
                activate();
                //reload
                window.location.reload();
            }
        } catch {
            // if the session extension fails, logout the user
            window.location.href = redirectUrl;
        } finally {
            setOpen(false);
        }
    }

    return (
        <TimeoutModal
            data-no-focus-lock
            isOpen={open}
            isSessionExpired={sessionState === 'Idle'}
            onExtendSession={extendSession}
            onLogoutUser={handleOnLogoutUser}
        />
    );
}
